import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Define default color indices for specific datasets
const DEFAULT_COLOR_INDICES: Record<string, number> = {
    usage_commodityUsage: 0,
    cost_commodity_totalCommodityCost: 1,
}

interface ChartColorState {
    colorIndices: Record<string, number>
    updateColors: (currentDatasets: string[]) => Record<string, number>
    resetColors: () => void
}

export const useChartColorStore = create<ChartColorState>()(
    persist(
        (set, get) => ({
            colorIndices: {},
            updateColors: (currentDatasets: string[]) => {
                const { colorIndices } = get()
                const newColorIndices: Record<string, number> = {}
                const usedIndices: number[] = []

                // Filter out "date" key
                const activeDatasets = currentDatasets.filter(
                    (key) => key !== 'date'
                )

                // Assign indices based on natural order
                activeDatasets.forEach((dataset, naturalIndex) => {
                    // Check if this dataset has a default color AND is at its natural position
                    const hasDefaultColor = dataset in DEFAULT_COLOR_INDICES
                    const defaultIndex = DEFAULT_COLOR_INDICES[dataset]
                    const shouldUseDefault =
                        hasDefaultColor && naturalIndex === defaultIndex

                    if (shouldUseDefault) {
                        // Use default color since it's at the right position
                        newColorIndices[dataset] = defaultIndex
                        usedIndices.push(defaultIndex)
                    } else if (
                        dataset in colorIndices &&
                        !usedIndices.includes(colorIndices[dataset])
                    ) {
                        // Reuse existing color if available and not used
                        const existingIndex = colorIndices[dataset]
                        newColorIndices[dataset] = existingIndex
                        usedIndices.push(existingIndex)
                    }
                })

                // Assign new colors for remaining datasets
                activeDatasets.forEach((dataset) => {
                    if (!(dataset in newColorIndices)) {
                        let index = 0
                        while (usedIndices.includes(index)) {
                            index++
                        }
                        newColorIndices[dataset] = index
                        usedIndices.push(index)
                    }
                })

                set({ colorIndices: newColorIndices })
                return newColorIndices
            },
            resetColors: () => {
                set({ colorIndices: {} })
            },
        }),
        {
            name: 'chart-color',
        }
    )
)
